import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';
import SummaryAnalyticsContent from '../../components/AnalyticsRA/SummaryAnalyticsContent';
import FilterModal from '../../components/AnalyticsRA/FilterModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SummaryAnalytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [isDateManuallyChanged, setIsDateManuallyChanged] = useState(false);
  const [isSyncingFilters, setIsSyncingFilters] = useState(false); // Флаг для предотвращения повторного запроса при синхронизации
  const [isFetching, setIsFetching] = useState(false); // Флаг для блокировки повторных запросов
  const [showFilterModal, setShowFilterModal] = useState(false);

  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;

  // Загрузка месяцев
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const monthsResponse = await api.get(`/quality/assessment-selection/assessment-months`);
        if (monthsResponse.data.success) {
          setMonths(monthsResponse.data.months);
        }
      } catch (error) {
        console.error('Ошибка загрузки месяцев:', error);
      }
    };
    fetchMonths();
  }, []);

  // Отслеживаем ручное изменение дат
  useEffect(() => {
    if (tempDateValues.date_filter_from !== dateFilters.date_filter_from ||
        tempDateValues.date_filter_to !== dateFilters.date_filter_to) {
      setIsDateManuallyChanged(true);
    }
  }, [dateFilters.date_filter_from, dateFilters.date_filter_to, tempDateValues]);

  // Сброс месяца при ручном вводе дат
  useEffect(() => {
    if (isDateManuallyChanged && (dateFilters.date_filter_from || dateFilters.date_filter_to) && selectedMonthYear) {
      setSelectedMonthYear('');
    }
  }, [isDateManuallyChanged, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);

  // Загрузка данных
  useEffect(() => {
    // Прерываем выполнение, если идет синхронизация фильтров или уже выполняется запрос
    if (isSyncingFilters || isFetching) {
      console.log('Пропуск загрузки данных: идет синхронизация фильтров или уже выполняется запрос');
      return;
    }
    
    const fetchData = async () => {
      // Повторная проверка внутри, на всякий случай
      if (isFetching) return;
      
      setIsFetching(true);
      setLoading(true);
      setError(null);
      try {
        const monthYearParts = selectedMonthYear ? selectedMonthYear.split('-') : [];
        const month = monthYearParts[0] || '';
        const year = monthYearParts[1] || '';
        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

        const response = await api.get(`/quality/assessment-selection/with-assessment`, {
          params: {
            date_filter_from: dateFrom,
            date_filter_to: dateTo,
            month: month,
            year: year
          }
        });

        setData(response.data.data || []);

        // Устанавливаем флаг синхронизации ВСЕХ фильтров
        setIsSyncingFilters(true);
        
        // Синхронизация месяца с сервера
        if (response.data.filters?.month && response.data.filters?.year) {
          const monthYearValue = `${response.data.filters.month}-${response.data.filters.year}`;
          if (monthYearValue !== selectedMonthYear) {
            setSelectedMonthYear(monthYearValue);
          }
        }

        // Синхронизация дат с сервера
        if (response.data.filters) {
          const serverFrom = response.data.filters.date_filter_from || getDefaultDateFrom();
          const serverTo = response.data.filters.date_filter_to || getDefaultDateTo();
          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);

          if (inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({ target: { name: 'date_filter_from', value: inputFrom } });
          }
          if (inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({ target: { name: 'date_filter_to', value: inputTo } });
          }
        }
        
        // Сбрасываем флаг после обновления ВСЕХ фильтров
        setTimeout(() => setIsSyncingFilters(false), 0);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
    };

    fetchData();
  }, [dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);



  const getMonthName = (monthNumber) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
      return months[monthNumber - 1] || '';
    };

  const monthYearOptions = months.map(m => ({
    value: `${m.month}-${m.year}`,
    label: `${getMonthName(m.month)} ${m.year}`
  }));

  const showCustomOption = isDateManuallyChanged && (dateFilters.date_filter_from || dateFilters.date_filter_to) && !selectedMonthYear;

  // Получение уникальных agent_name из данных
  const agentNames = useMemo(() => {
    if (!data.length) return [];
    const nameSet = new Set();
    data.forEach(item => {
      if (item.assessment.agent_name) {
        nameSet.add(item.assessment.agent_name);
      }
    });
    return Array.from(nameSet);
  }, [data]);

  // Состояние для выбранных агентов
  const [selectedAgents, setSelectedAgents] = useState([]);

  // Обработчик изменения чекбокса agent_name
  const handleAgentChange = (agentName) => {
    setSelectedAgents(prev => 
      prev.includes(agentName)
        ? prev.filter(name => name !== agentName) // Удаляем
        : [...prev, agentName] // Добавляем
    );
  };

  // Отфильтрованные данные
  const filteredData = useMemo(() => {
    if (!selectedAgents.length) return data;
    return data.filter(item => 
      item.assessment.agent_name && 
      selectedAgents.includes(item.assessment.agent_name)
    );
  }, [data, selectedAgents]);

  return (
    <div className="analytics-container">
      <h2>Сводная аналитика</h2>

      {/* Модальное окно фильтров */}
      <FilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        dateFilters={dateFilters}
        tempValues={tempDateValues}
        handleDateChange={handleDateChange}
        handleDateBlur={handleDateBlur}
        selectedMonthYear={selectedMonthYear}
        monthYearOptions={monthYearOptions}
        handleMonthChange={(e) => {
          const value = e.target.value;
          setSelectedMonthYear(value);
          if (value === '') {
            handleDateBlur({ target: { name: 'date_filter_from', value: '' } });
            handleDateBlur({ target: { name: 'date_filter_to', value: '' } });
          }
        }}
        agentNames={agentNames}
        selectedAgents={selectedAgents}
        handleAgentChange={handleAgentChange}
        showCustomOption={showCustomOption}
      />

      {/* Кнопка открытия модального окна фильтров */}
      <div className="mb-4">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowFilterModal(true)}
          style={{
            padding: '0.375rem 0.75rem',
            fontSize: '1rem',
            borderRadius: '0.25rem'
          }}
        >
          Фильтры
        </button>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {error && <p className="text-danger">{error}</p>}
          <p>Загружено записей: {data.length}</p>

          <SummaryAnalyticsContent data={filteredData} dateFrom={dateFilters.date_filter_from} dateTo={dateFilters.date_filter_to} />
        </>
      )
      }
    </div>
  );
};

export default SummaryAnalytics;
